import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, withFreshAccess } from '../api/client'
import { useAccounts } from '../store/accounts'
import { useTabs } from '../store/tabs'
import { useUI } from '../store/ui'
import { useActivityState } from '../store/activityState'
import { isApiBlocked } from '../store/apiHealth'
import { PostMedia, type PostEmbedView } from './PostMedia'
import { RelativeTime } from './RelativeTime'

// Background poll for the right-panel activity widget. 5 min keeps
// the worker bill sane — a faster cadence (30s) was burning ~70k
// requests/day per active session because the activity poll hits
// getAuthorFeed for EVERY open tab on every tick.
const POLL_MS = 5 * 60 * 1000
const MARK_AFTER_MS = 1500
const RETENTION_MS = 24 * 60 * 60 * 1000

interface FeedItem {
  post: {
    uri: string
    cid: string
    author: { did: string; handle: string; displayName?: string; avatar?: string }
    record?: { text?: string; createdAt?: string }
    embed?: PostEmbedView
    indexedAt: string
    likeCount?: number
    repostCount?: number
    replyCount?: number
  }
  reason?: { $type?: string }
}

function mergeAndPrune(
  prev: ActivityItem[],
  fresh: ActivityItem[],
): ActivityItem[] {
  const cutoff = Date.now() - RETENTION_MS
  const byUri = new Map<string, ActivityItem>()
  for (const it of prev) {
    if (Date.parse(it.indexedAt) >= cutoff) byUri.set(it.uri, it)
  }
  for (const it of fresh) {
    if (Date.parse(it.indexedAt) >= cutoff) byUri.set(it.uri, it)
  }
  return Array.from(byUri.values())
}

function pruneOlderThan(items: ActivityItem[], cutoffMs: number): ActivityItem[] {
  const next = items.filter((it) => Date.parse(it.indexedAt) >= cutoffMs)
  return next.length === items.length ? items : next
}

function rkeyOf(uri: string): string | null {
  const m = uri.match(/^at:\/\/[^/]+\/[^/]+\/([^/]+)$/)
  return m ? m[1] : null
}

interface ActivityItem {
  uri: string
  indexedAt: string
  fromHandle: string
  text: string
  authorHandle: string
  authorAvatar?: string
  embed?: PostEmbedView
  kind: 'post' | 'reply' | 'repost'
}

/**
 * Background activity panel. Every 30s polls each open profile-tab's
 * getAuthorFeed (limit 15), keeps a rolling buffer of items whose
 * indexedAt > the tab's lastSeenAt watermark, sorts newest-first.
 *
 * After the user views the panel for ~1.5s, items get marked as seen
 * (advance the per-tab lastSeenAt watermark) so the red-dot indicator
 * on the right-panel pill clears. Refresh button forces an immediate
 * poll.
 */
export function ActivityFeed() {
  const acc = useAccounts((s) => s.accounts.find((a) => a.id === s.activeId))
  const tabs = useTabs((s) => s.tabs)
  const markSeen = useTabs((s) => s.markSeen)
  const setActivityUnseen = useUI((s) => s.setActivityUnseen)
  const pollIntervalSec = useActivityState((s) => s.pollIntervalSec)
  const pollMs = pollIntervalSec * 1000
  const [items, setItems] = useState<ActivityItem[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const navigate = useNavigate()

  // Poll every POLL_MS while panel mounted. Each tick fetches each
  // tab's recent posts and merges them into the rolling buffer (dedupe
  // by uri). Visibility is governed by the 24h window — items leave on
  // their own when they age out, not when they're marked seen.
  useEffect(() => {
    let cancelled = false
    async function pollOnce() {
      if (!acc || tabs.length === 0) {
        setItems([])
        return
      }
      // Skip the entire poll if the worker has signalled it's blocked
      // (429 / network down) OR the active account is deactivated.
      // The banner tells the user; no point hammering when each
      // request is being rejected.
      if (isApiBlocked() || acc.deactivated) return
      setBusy(true)
      setErr(null)
      const fresh: ActivityItem[] = []
      try {
        await Promise.all(
          tabs.map(async (t) => {
            const actor = t.did ?? t.handle
            try {
              const r = await withFreshAccess(acc.id, () =>
                api.xrpc<{ feed?: FeedItem[] }>({
                  accountId: acc.id,
                  method: 'GET',
                  path: `app.bsky.feed.getAuthorFeed?actor=${encodeURIComponent(actor)}&limit=30`,
                }),
              )
              for (const it of r.feed ?? []) {
                const p = it.post
                if (!p?.uri || !p.indexedAt) continue
                const isRepost =
                  it.reason?.$type === 'app.bsky.feed.defs#reasonRepost'
                const isReply = !!(p.record as { reply?: unknown } | undefined)?.reply
                fresh.push({
                  uri: p.uri,
                  indexedAt: p.indexedAt,
                  fromHandle: t.handle,
                  text: p.record?.text ?? '',
                  authorHandle: p.author?.handle ?? '?',
                  authorAvatar: p.author?.avatar,
                  embed: p.embed,
                  kind: isRepost ? 'repost' : isReply ? 'reply' : 'post',
                })
              }
            } catch {
              /* skip this tab on error, we'll retry next tick */
            }
          }),
        )
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e))
      } finally {
        if (!cancelled) {
          setItems((prev) => mergeAndPrune(prev, fresh))
          setBusy(false)
        }
      }
    }
    void pollOnce()
    let id: ReturnType<typeof setInterval> | null = setInterval(pollOnce, pollMs)
    function onVisibility() {
      if (document.visibilityState === 'hidden') {
        if (id) {
          clearInterval(id)
          id = null
        }
      } else if (!id) {
        id = setInterval(pollOnce, pollMs)
        // Also fire one immediately on re-show so the user sees fresh
        // data when they come back.
        void pollOnce()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    if (document.visibilityState === 'hidden' && id) {
      clearInterval(id)
      id = null
    }
    return () => {
      cancelled = true
      if (id) clearInterval(id)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [acc, tabs, pollMs])

  // Tick `now` so the per-row "ago" pill (and any other re-renders)
  // refresh smoothly without depending on the poll cadence. Also
  // prunes items that have aged past the 24h retention window so the
  // list shrinks on its own as time passes.
  useEffect(() => {
    const id = setInterval(() => {
      setNow(Date.now())
      setItems((prev) => pruneOlderThan(prev, Date.now() - RETENTION_MS))
    }, 1000)
    return () => clearInterval(id)
  }, [])

  // 24h-rolling visible list — derived from `items` + `now`.
  const visible = useMemo(() => {
    const cutoff = now - RETENTION_MS
    return items
      .filter((it) => Date.parse(it.indexedAt) >= cutoff)
      .sort((a, b) => (a.indexedAt < b.indexedAt ? 1 : -1))
  }, [items, now])

  // Unseen count — anything in `visible` whose indexedAt is past the
  // per-tab lastSeenAt watermark.
  const unseenCount = useMemo(() => {
    const seenByHandle = new Map<string, string>()
    for (const t of tabs) seenByHandle.set(t.handle, t.lastSeenAt ?? '')
    let n = 0
    for (const it of visible) {
      const seen = seenByHandle.get(it.fromHandle) ?? ''
      if (!seen || it.indexedAt > seen) n++
    }
    return n
  }, [visible, tabs])

  // Publish unseen count so the right-panel pill can show a red dot.
  useEffect(() => {
    setActivityUnseen(unseenCount)
    return () => setActivityUnseen(0)
  }, [unseenCount, setActivityUnseen])

  // Mark-as-read after the user has had the panel open long enough to
  // see the items. Updates each tab's lastSeenAt to the newest item we
  // showed for that tab — the items themselves stay in the 24h list.
  useEffect(() => {
    if (visible.length === 0) return
    const t = setTimeout(() => {
      const newestPerTab = new Map<string, string>()
      for (const it of visible) {
        const cur = newestPerTab.get(it.fromHandle) ?? ''
        if (it.indexedAt > cur) newestPerTab.set(it.fromHandle, it.indexedAt)
      }
      for (const [handle, iso] of newestPerTab) {
        markSeen(handle, iso)
      }
      setActivityUnseen(0)
    }, MARK_AFTER_MS)
    return () => clearTimeout(t)
  }, [visible, markSeen, setActivityUnseen])

  return (
    <div className="flex h-full flex-col">
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-white/10 bg-ink-900/85 px-3 py-2 backdrop-blur-xl">
        <div className="text-[11px] uppercase tracking-widest text-ink-300">
          📡 activity
          <span className="ml-2 text-ink-500">
            ({visible.length} in 24h · {unseenCount} new across {tabs.length} tab
            {tabs.length === 1 ? '' : 's'})
          </span>
        </div>
        <button
          type="button"
          onClick={() => setNow(Date.now())}
          className="rounded border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] text-ink-300 hover:bg-white/10"
          disabled={busy}
          title={
            busy
              ? 'fetching…'
              : `auto-refreshes every ${pollIntervalSec}s`
          }
        >
          {busy ? '…' : '⟳'}
        </button>
      </header>

      {err ? (
        <div className="m-3 rounded-xl border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-100">
          {err}
        </div>
      ) : null}

      {!acc ? (
        <div className="p-3 text-xs text-ink-300">sign in to see activity.</div>
      ) : null}

      {acc && tabs.length === 0 ? (
        <div className="p-3 text-xs text-ink-300">
          no open tabs — search a handle and click a result so the activity
          poller has something to watch.
        </div>
      ) : null}

      {acc && tabs.length > 0 && visible.length === 0 && !busy ? (
        <div className="p-3 text-xs text-ink-300">
          ✓ nothing in the last 24h. polling every {pollIntervalSec}s in
          the background.
        </div>
      ) : null}

      <div className="flex-1 overflow-y-auto px-2 py-2 text-xs">
        <ul className="space-y-2">
          {visible.map((it) => {
            const rk = rkeyOf(it.uri)
            const target = rk
              ? `/post/${encodeURIComponent(it.authorHandle)}/${rk}`
              : null
            return (
              <li
                key={it.uri}
                className="rounded-xl border border-white/10 bg-white/5 p-2 hover:border-white/20 hover:bg-white/10"
              >
                <div className="mb-1 flex items-center gap-1.5 text-[10px]">
                  <span
                    className={
                      it.kind === 'repost'
                        ? 'rounded-full border border-emerald-400/30 bg-emerald-500/10 px-1.5 py-0.5 text-emerald-100'
                        : it.kind === 'reply'
                          ? 'rounded-full border border-fuchsia-400/30 bg-fuchsia-500/10 px-1.5 py-0.5 text-fuchsia-100'
                          : 'rounded-full border border-sky-400/30 bg-sky-500/10 px-1.5 py-0.5 text-sky-100'
                    }
                  >
                    {it.kind === 'repost' ? '↻ repost' : it.kind === 'reply' ? '↩ reply' : '✎ post'}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(`/profile/${encodeURIComponent(it.fromHandle)}`)
                    }}
                    className="text-ink-200 hover:underline"
                  >
                    @{it.fromHandle}
                  </button>
                  <RelativeTime
                    iso={it.indexedAt}
                    className="ml-auto text-[10px] text-ink-400"
                  />
                </div>
                {it.kind === 'repost' && it.authorHandle !== it.fromHandle ? (
                  <div className="mb-1 truncate text-[10px] text-ink-400">
                    via @{it.authorHandle}
                  </div>
                ) : null}
                <button
                  type="button"
                  className="block w-full text-left"
                  onClick={() => {
                    if (target) navigate(target)
                  }}
                  title={target ? 'open thread in main pane' : undefined}
                  disabled={!target}
                >
                  {it.text ? (
                    <p className="line-clamp-4 whitespace-pre-wrap text-[12px] text-ink-100">
                      {it.text}
                    </p>
                  ) : null}
                  <PostMedia embed={it.embed} />
                </button>
              </li>
            )
          })}
        </ul>
      </div>
      <div className="border-t border-white/10 px-3 py-1 text-[9px] text-ink-500">
        24h rolling window · click any row to open the thread on the left
      </div>
    </div>
  )
}
